import _ from "lodash";
import struct_pb from "google-protobuf/google/protobuf/struct_pb";

import auth_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/auth_pb";
import feature_flags_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/feature_flags_pb";
import files_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/files_pb";
import flight_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/flight_pb";
import organization_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/organization_pb";
import release_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/release_pb";
import scan_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/scan_pb";
import ucon_pb from "@skydio/pbtypes/pbtypes/vehicle/infrastructure/flight_deck/ucon_pb";
import user_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/user_pb";
import vehicle_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/vehicle_pb";

import { createRoute, createProtobufRoute } from "../../common/http";

import {
  AuthenticateRequest,
  AuthRefreshRequest,
  LoginRequest,
  LoginTypeRequest,
  LoginTypeResponse,
  PermanentLoginRequest,
  TokenExchangeRequest,
} from "./auth/types";
import {
  DataFileRequest,
  DataFileResponse,
  DataFilesRequest,
  DownloadDataFilesRequest,
} from "./data_files/types";
import { FeatureFlagRequest } from "./feature_flags/types";
import { FlightsRequest, FlightRequest, FlightStatsRequest } from "./flights/types";
import { GroupsResponse } from "./groups/types";
import {
  ReleasesRequest,
  ReleaseRequest,
  ReleaseFilesRequest,
  UpdateReleaseRequest,
  SetDeviceReleaseOverrideRequest,
  DownloadReleaseRequest,
} from "./releases/types";
import { DevReleaseFilesRequest, DevReleaseFileRequest } from "./dev_releases/types";
import {
  ControllerReleasesProtoRequest,
  ControllerReleaseRequest,
  UpdateControllerReleaseRequest,
  DownloadControllerReleaseRequest,
  SetControllerReleaseOverrideRequest,
  DeleteControllerReleaseOverrideRequest,
} from "./controller_releases/types";
import { UsersRequest, UserRequest, AddOrUpdateUserRequest } from "./users/types";
import { VehiclesRequest, VehicleRequest, UpdateVehicleRequest } from "./vehicles/types";
import {
  UpdateOrganizationRequest,
  DeleteOrganizationRequest,
  AcceptInviteRequest,
  RemoveUserRequest,
  GetOrganizationStatsRequest,
  UploadLogoRequest,
  OrganizationRequest,
} from "./organizations/types";
import { UpdateOrgUCONKindConfigRequest } from "./ucon_upload_config/types";
import {
  OnpremResponse,
  OnpremResponseVersion,
  OnpremResponseDisk,
  OnpremResponseSCCurrentTimestamp,
  OnpremResponseDiscover,
  OnpremResponseStageSelfSigned,
  OnpremResponseBundleCreate,
  OnpremResponseBundleStatus,
  OnpremRequestRtspSettings,
  OnpremResponseRtspSettings,
  OnpremRequestRemoteIdSettings,
  OnpremRequestDocklessSitesSettings,
  OnpremRequestPathfinderSettings,
  OnpremResponseFeatureFlags,
  OnpremResponseServerConfigUpdate,
  OnpremResponsePathfinderTilesetStatus,
  OnpremResponsePathfinderUploadCredentials,
} from "./onprem_admin/types";
import { GetScanRequest } from "./scans/types";

// All API endpoints we request from are described here
const cloudApiEndpoints = {
  LOGIN: createRoute<LoginRequest, void>("auth/login", "POST"),
  LOGIN_TYPES: createRoute<LoginTypeRequest, LoginTypeResponse>("auth/login_type", "POST"),
  AUTHENTICATE: createRoute<AuthenticateRequest, void>("cauth/authenticate", "POST"),
  // We want complete tokens rather than CSRF tokens, for apps like Streamlit that
  // make python cloud api clients w/ user credentials.
  AUTHENTICATE_NO_COOKIES: createRoute<AuthenticateRequest, auth_pb.AuthenticateResponse.AsObject>(
    "auth/authenticate",
    "POST"
  ),
  FAST_AUTHENTICATE: createRoute<LoginRequest, void>("cauth/fastauth", "POST"),
  LOGOUT: createRoute<undefined, void>("cauth/logout", "POST"),
  REFRESH_AUTH: createRoute<AuthRefreshRequest, void>("cauth/refresh", "POST"),
  REQUEST_JWT: createProtobufRoute<TokenExchangeRequest, auth_pb.TokenExchangeResponse>(
    "auth/request_jwt",
    "POST",
    auth_pb.TokenExchangeResponse.deserializeBinary
  ),
  SET_PERMANENT_LOGIN_CODE: createProtobufRoute<PermanentLoginRequest, struct_pb.Struct>(
    "auth/perm_login_code",
    "POST",
    struct_pb.Struct.deserializeBinary
  ),
  DELETE_PERMANENT_LOGIN_CODE: createProtobufRoute<PermanentLoginRequest, struct_pb.Struct>(
    "auth/perm_login_code",
    "DELETE",
    struct_pb.Struct.deserializeBinary
  ),
  GET_FLIGHTS: createProtobufRoute<FlightsRequest, flight_pb.Flights>(
    "flights",
    "GET",
    flight_pb.Flights.deserializeBinary
  ),
  GET_FLIGHT: createProtobufRoute<FlightRequest, flight_pb.Flight>(
    ({ flightId }) => `flight/${flightId}`,
    "GET",
    flight_pb.Flight.deserializeBinary
  ),
  GET_FLIGHT_STATS: createProtobufRoute<FlightStatsRequest, flight_pb.FlightStats>(
    "flight_stats",
    "GET",
    flight_pb.FlightStats.deserializeBinary
  ),
  DOWNLOAD_FLIGHT_CSV: createRoute<FlightRequest, Response>(
    ({ flightId }) => `flight/${flightId}/telemetry/csv`,
    "GET"
  ),
  DOWNLOAD_FLIGHTS_CSV: createRoute<FlightsRequest, Response>("flights/download", "GET"),
  GET_FLIGHT_DATA_FILE: createRoute<DataFileRequest, DataFileResponse>(
    ({ uuid }) => `flight_data/${uuid}`,
    "GET"
  ),
  DOWNLOAD_FLIGHT_DATA_FILE: createRoute<DataFileRequest, Response>(
    ({ uuid }) => `flight_data/${uuid}/download`,
    "GET"
  ),
  GET_FLIGHT_DATA_FILES: createProtobufRoute<DataFilesRequest, files_pb.DataFiles>(
    "flight_data_files",
    "GET",
    files_pb.DataFiles.deserializeBinary
  ),
  DOWNLOAD_FLIGHT_FILES_ZIP: createRoute<DownloadDataFilesRequest, Response>(
    "flight_data_files/download",
    "POST"
  ),
  GET_FILE_KINDS: createProtobufRoute<undefined, files_pb.GetFileKindsResponse>(
    "file_kinds",
    "GET",
    files_pb.GetFileKindsResponse.deserializeBinary
  ),
  GET_GROUPS: createRoute<undefined, GroupsResponse>("groups", "GET"),
  GET_RELEASES: createProtobufRoute<ReleasesRequest, release_pb.Releases>(
    "releases",
    "GET",
    release_pb.Releases.deserializeBinary
  ),
  GET_RELEASE: createProtobufRoute<ReleaseRequest, release_pb.ReleaseFile>(
    ({ key }) => `release/${key}`,
    "GET",
    release_pb.ReleaseFile.deserializeBinary
  ),
  GET_RELEASE_FILES: createProtobufRoute<
    ReleaseFilesRequest | DevReleaseFilesRequest,
    release_pb.ReleaseFiles
  >("release_files", "GET", release_pb.ReleaseFiles.deserializeBinary),
  GET_RELEASE_FILE: createProtobufRoute<DevReleaseFileRequest, release_pb.ReleaseFile>(
    ({ key }) => `release_file/${key}`,
    "GET",
    release_pb.ReleaseFile.deserializeBinary
  ),
  GET_CONTROLLER_RELEASES: createProtobufRoute<
    ControllerReleasesProtoRequest,
    release_pb.ControllerReleases
  >("controller_releases", "POST", release_pb.ControllerReleases.deserializeBinary),
  GET_CONTROLLER_RELEASE: createProtobufRoute<
    ControllerReleaseRequest,
    release_pb.ControllerReleaseFile
  >(
    ({ key }) => `controller_release/${key}`,
    "GET",
    release_pb.ControllerReleaseFile.deserializeBinary
  ),
  UPDATE_RELEASE: createProtobufRoute<UpdateReleaseRequest, release_pb.ReleaseFile>(
    ({ key }) => `release/${key}`,
    "POST",
    release_pb.ReleaseFile.deserializeBinary
  ),
  DOWNLOAD_RELEASE: createProtobufRoute<DownloadReleaseRequest, release_pb.FindReleaseResponse>(
    ({ key }) => `release/get/${key}`,
    "POST",
    release_pb.FindReleaseResponse.deserializeBinary
  ),
  UPDATE_CONTROLLER_RELEASE: createProtobufRoute<
    UpdateControllerReleaseRequest,
    release_pb.UpdateControllerReleaseResponse
  >(
    ({ key }) => `controller_release/${key}`,
    "POST",
    release_pb.UpdateControllerReleaseResponse.deserializeBinary
  ),
  DOWNLOAD_CONTROLLER_RELEASE: createProtobufRoute<
    DownloadControllerReleaseRequest,
    release_pb.FindControllerReleaseResponse
  >("controller_release/get", "POST", release_pb.FindControllerReleaseResponse.deserializeBinary),
  SET_DEVICE_RELEASE_OVERRIDE: createProtobufRoute<
    SetDeviceReleaseOverrideRequest,
    release_pb.DeviceReleaseOverrides
  >(
    ({ key }) => `/release/set_device/${key}`,
    "POST",
    release_pb.DeviceReleaseOverrides.deserializeBinary
  ),
  SET_CONTROLLER_RELEASE_OVERRIDE: createProtobufRoute<
    SetControllerReleaseOverrideRequest,
    release_pb.ControllerReleaseOverrides
  >(
    ({ key }) => `/controller_release/override/${key}`,
    "POST",
    release_pb.ControllerReleaseOverrides.deserializeBinary
  ),
  DELETE_CONTROLLER_RELEASE_OVERRIDE: createProtobufRoute<
    DeleteControllerReleaseOverrideRequest,
    release_pb.ControllerReleaseOverrides
  >(
    ({ key }) => `/controller_release/override/${key}`,
    "DELETE",
    release_pb.ControllerReleaseOverrides.deserializeBinary
  ),
  GET_CURRENT_USER: createProtobufRoute<undefined, user_pb.User>(
    "user",
    "GET",
    user_pb.User.deserializeBinary
  ),
  GET_USERS: createProtobufRoute<UsersRequest, user_pb.Users>(
    "users",
    "GET",
    user_pb.Users.deserializeBinary
  ),
  GET_USER: createProtobufRoute<UserRequest, user_pb.User>(
    ({ identifier }) => `user/${identifier}`,
    "GET",
    user_pb.User.deserializeBinary
  ),
  ADD_USER: createProtobufRoute<AddOrUpdateUserRequest, user_pb.User>(
    ({ identifier }) => `user/${identifier}`,
    "POST",
    user_pb.User.deserializeBinary
  ),
  UPDATE_USER: createProtobufRoute<AddOrUpdateUserRequest, user_pb.User>(
    ({ identifier }) => `user/${identifier}`,
    "POST",
    user_pb.User.deserializeBinary
  ),
  GET_VEHICLES: createProtobufRoute<VehiclesRequest, vehicle_pb.Vehicles>(
    "vehicles",
    "GET",
    vehicle_pb.Vehicles.deserializeBinary
  ),
  GET_DEVICE_DATA_FILE: createRoute<DataFileRequest, DataFileResponse>(
    ({ uuid }) => `vehicle_data/${uuid}`,
    "GET"
  ),
  GET_DEVICE_DATA_FILES: createProtobufRoute<DataFilesRequest, files_pb.DataFiles>(
    "vehicle_data_files",
    "GET",
    files_pb.DataFiles.deserializeBinary
  ),
  GET_VEHICLE: createProtobufRoute<VehicleRequest, vehicle_pb.Vehicle>(
    ({ vehicleId }) => `vehicle/${vehicleId}`,
    "GET",
    vehicle_pb.Vehicle.deserializeBinary
  ),
  UPDATE_VEHICLE: createProtobufRoute<UpdateVehicleRequest, vehicle_pb.Vehicle>(
    ({ vehicleId }) => `vehicle/${vehicleId}`,
    "POST",
    vehicle_pb.Vehicle.deserializeBinary
  ),
  GET_ORGANIZATION: createProtobufRoute<OrganizationRequest, organization_pb.Organization>(
    ({ uuid }) => `organization/${uuid}`,
    "GET",
    organization_pb.Organization.deserializeBinary
  ),
  GET_ORGANIZATIONS: createProtobufRoute<undefined, organization_pb.Organizations>(
    "organizations",
    "GET",
    organization_pb.Organizations.deserializeBinary
  ),
  UPDATE_ORGANIZATION: createProtobufRoute<UpdateOrganizationRequest, organization_pb.Organization>(
    ({ uuid }) => `organization/${uuid}`,
    "POST",
    organization_pb.Organization.deserializeBinary
  ),
  ADD_ORGANIZATION: createProtobufRoute<UpdateOrganizationRequest, organization_pb.Organization>(
    "organization",
    "POST",
    organization_pb.Organization.deserializeBinary
  ),
  DELETE_ORGANIZATION: createProtobufRoute<DeleteOrganizationRequest, struct_pb.Struct>(
    ({ uuid }) => `organization/${uuid}/delete`,
    "POST",
    struct_pb.Struct.deserializeBinary
  ),
  GET_ORG_STATS: createProtobufRoute<undefined, organization_pb.OrganizationStats>(
    ({ uuid }) => `organization/${uuid}/stats`,
    "GET",
    organization_pb.OrganizationStats.deserializeBinary
  ),
  GET_ORG_UCON_UPLOAD_CONFIG: createProtobufRoute<undefined, ucon_pb.UploadConfig>(
    ({ uuid }) => `organization/${uuid}/ucon_config`,
    "GET",
    ucon_pb.UploadConfig.deserializeBinary
  ),
  UPDATE_ORG_UCON_UPLOAD_CONFIG: createProtobufRoute<
    UpdateOrgUCONKindConfigRequest,
    ucon_pb.UploadConfig
  >(
    ({ uuid }) => `organization/${uuid}/update_kind`,
    "POST",
    ucon_pb.UploadConfig.deserializeBinary
  ),
  REMOVE_ORG_UCON_UPLOAD_CONFIG: createProtobufRoute<
    UpdateOrgUCONKindConfigRequest,
    ucon_pb.UploadConfig
  >(
    ({ uuid }) => `organization/${uuid}/remove_kind`,
    "POST",
    ucon_pb.UploadConfig.deserializeBinary
  ),
  REMOVE_USER_FROM_ORG: createRoute<RemoveUserRequest, void>(
    ({ uuid, email }) => `organization/${uuid}/remove/${email}`,
    "POST"
  ),
  ACCEPT_ORG_INVITE: createRoute<AcceptInviteRequest, void>(
    ({ jwtPayload }) => `organization/accept_invite/${jwtPayload}`,
    "GET"
  ),
  GET_FEATURE_FLAGS: createProtobufRoute<undefined, feature_flags_pb.FeatureFlags>(
    "feature_flags",
    "GET",
    feature_flags_pb.FeatureFlags.deserializeBinary
  ),
  GET_FEATURE_FLAGS_FOR_ORG_ROLE: createProtobufRoute<
    FeatureFlagRequest,
    feature_flags_pb.FeatureFlags
  >("feature_flags", "POST", feature_flags_pb.FeatureFlags.deserializeBinary),

  // DOWNLOAD CSV FILES
  DOWNLOAD_STATS_CSV: createRoute<undefined, Response>(
    ({ organizationId }) => `organization/${organizationId}/download_stats`,
    "GET"
  ),

  // AGGREGATES OVER TIME: PILOTS, STORAGE
  GET_ORG_PILOT_STATS: createProtobufRoute<
    GetOrganizationStatsRequest,
    organization_pb.OrganizationPilotStats
  >(
    ({ organizationId }) => `organization/${organizationId}/pilot_stats`,
    "GET",
    organization_pb.OrganizationPilotStats.deserializeBinary
  ),

  GET_ORG_STORAGE_STATS: createProtobufRoute<
    GetOrganizationStatsRequest,
    organization_pb.StorageStats
  >(
    ({ organizationId }) => `organization/${organizationId}/storage_stats`,
    "GET",
    organization_pb.StorageStats.deserializeBinary
  ),

  UPLOAD_LOGO: createProtobufRoute<UploadLogoRequest, struct_pb.Struct>(
    ({ organizationId }) => `organization/${organizationId}/logo`,
    "POST",
    struct_pb.Struct.deserializeBinary
  ),

  DOWNLOAD_ORGANIZATION_FILE: createRoute<DataFileRequest, Response>(
    ({ uuid }) => `organization_file/${uuid}`,
    "GET"
  ),

  DOWNLOAD_THUMBNAIL: createRoute<DataFileRequest, Response>(
    ({ uuid }) => `flight_data/${uuid}/thumbnail`,
    "GET"
  ),

  GET_SCAN: createProtobufRoute<GetScanRequest, scan_pb.ScanPb>(
    ({ uuid }) => `scan/${uuid}`,
    "GET",
    scan_pb.ScanPb.deserializeBinary
  ),

  ONPREM_SERVER_CONFIG_CURRENT_TIMESTAMP: createRoute<undefined, OnpremResponseSCCurrentTimestamp>(
    "onprem/server-config/current/timestamp",
    "GET"
  ),

  ONPREM_SERVICE_HEALTH: createRoute<undefined, OnpremResponse>("onprem/service/health", "GET"),

  ONPREM_SERVICE_RESTART: createRoute<undefined, OnpremResponse>("onprem/service/restart", "POST"),

  ONPREM_SERVICE_UPGRADE_KEEPALIVE: createRoute<undefined, OnpremResponse>(
    "onprem/service/upgrade/keepalive",
    "GET"
  ),

  ONPREM_SERVICE_UPGRADE_PERFORM: createRoute<undefined, OnpremResponse>(
    "onprem/service/upgrade/perform",
    "POST"
  ),

  ONPREM_SERVICE_UPGRADE_VALIDATE: createRoute<{ file: Blob }, OnpremResponseVersion>(
    "onprem/service/upgrade/validate",
    "PUT"
  ),

  ONPREM_VERSION: createRoute<undefined, OnpremResponseVersion>("onprem/version", "GET"),

  ONPREM_SERVER_CONFIG_UPDATE: createRoute<
    { file: Blob; path: { forceUpdate: string } },
    OnpremResponseServerConfigUpdate
  >(({ forceUpdate }) => `onprem/server-config/update?force_update=${forceUpdate}`, "PUT"),

  ONPREM_FLASHPACK_IMPORT: createRoute<{ file: Blob }, OnpremResponse>(
    "onprem/flashpack/import",
    "PUT"
  ),

  ONPREM_SUPPORT_CREATE_BUNDLE: createRoute<undefined, OnpremResponseBundleCreate>(
    "onprem/support/bundle/create",
    "POST"
  ),

  ONPREM_SUPPORT_BUNDLE_STATUS: createRoute<{ jobId: string }, OnpremResponseBundleStatus>(
    "onprem/support/bundle/status",
    "GET"
  ),

  ONPREM_SUPPORT_DOWNLOAD_BUNDLE: createRoute<{ jobId: string }, Response>(
    "onprem/support/bundle/download",
    "GET"
  ),

  ONPREM_SUPPORT_DISK_INFO: createRoute<undefined, OnpremResponseDisk>(
    "onprem/support/disk-info",
    "GET"
  ),

  ONPREM_DISCOVER: createRoute<undefined, OnpremResponseDiscover>("onprem/discover", "GET"),

  ONPREM_VALIDATE_DNS: createRoute<undefined, OnpremResponse>("onprem/validate/dns", "GET"),

  ONPREM_VALIDATE_IPS: createRoute<undefined, OnpremResponse>("onprem/validate/ips", "GET"),

  ONPREM_CERTS_STAGE_CUSTOM: createRoute<
    { domain_name: string; app_cert: string; app_key: string; ca_cert: string },
    OnpremResponse
  >("onprem/certs/stage/custom", "PUT"),

  ONPREM_CERTS_STAGE_SELF_SIGNED: createRoute<
    { path: { domain_name: string } },
    OnpremResponseStageSelfSigned
  >(({ domain_name }) => `onprem/certs/stage/self-signed?domain_name=${domain_name}`, "POST"),

  ONPREM_CERTS_INSTALL_STAGED: createRoute<{ path: { domain_name: string } }, OnpremResponse>(
    ({ domain_name }) => `onprem/certs/install/staged?domain_name=${domain_name}`,
    "POST"
  ),

  // Mapbox Atlas Routes
  MAPBOX_ATLAS_HEALTH: createRoute<undefined, OnpremResponse>(
    "onprem/mapbox/service/health",
    "GET"
  ),

  MAPBOX_ATLAS_LICENSE: createRoute<undefined, OnpremResponse>(
    "onprem/mapbox/service/license",
    "GET"
  ),

  MAPBOX_ATLAS_VERSION: createRoute<undefined, OnpremResponseVersion>(
    "onprem/mapbox/service/version",
    "GET"
  ),

  MAPBOX_MBTILES_STATUS: createRoute<undefined, OnpremResponse>(
    "onprem/mapbox/mbtiles/status",
    "GET"
  ),

  MAPBOX_ATLAS_SERVICE_RESTART: createRoute<undefined, OnpremResponse>(
    "onprem/mapbox/service/restart",
    "POST"
  ),

  MAPBOX_CHECK_CONNECTIVITY: createRoute<{ url: string }, OnpremResponse & { token?: string }>(
    "onprem/mapbox/check-connectivity",
    "POST"
  ),

  MAPBOX_CONFIG: createRoute<undefined, OnpremResponse & { mapboxHost?: string }>(
    "onprem/mapbox/config",
    "GET"
  ),
  MAPBOX_UPDATE_CONFIG: createRoute<{ mapbox_host: string; access_token: string }, OnpremResponse>(
    "onprem/mapbox/config/update",
    "POST"
  ),
  MAPBOX_ATLAS_INSTALL_VALIDATE: createRoute<
    { file: Blob; path: { filename: string } },
    OnpremResponse
  >(
    ({ filename }) =>
      `onprem/mapbox/service/install/validate?filename=${encodeURIComponent(filename)}`,
    "PUT"
  ),
  MAPBOX_ATLAS_INSTALL_PERFORM: createRoute<undefined, OnpremResponse>(
    "onprem/mapbox/service/install/perform",
    "POST"
  ),
  MAPBOX_ATLAS_INSTALL_KEEPALIVE: createRoute<undefined, OnpremResponse & { status?: string }>(
    "onprem/mapbox/service/install/keepalive",
    "GET"
  ),
  MAPBOX_ATLAS_INSTALL_CANCEL: createRoute<undefined, OnpremResponse>(
    "onprem/mapbox/service/install/cancel",
    "POST"
  ),
  MAPBOX_MBTILES_REPLACE: createRoute<{ file: Blob }, OnpremResponse>(
    "onprem/mapbox/mbtiles/replace",
    "PUT"
  ),
  MAPBOX_MBTILES_KEEPALIVE: createRoute<undefined, OnpremResponse & { status?: string }>(
    "onprem/mapbox/mbtiles/keepalive",
    "GET"
  ),

  // Pathfinder Tilesets Routes
  PATHFINDER_TILESETS_STATUS: createRoute<undefined, OnpremResponsePathfinderTilesetStatus>(
    "onprem/pathfinder/tilesets/status",
    "GET"
  ),
  PATHFINDER_TILESETS_UPLOAD_CREDENTIALS: createRoute<
    undefined,
    OnpremResponsePathfinderUploadCredentials
  >("onprem/pathfinder/tilesets/upload-credentials", "POST"),
  PATHFINDER_TILESETS_INGEST: createRoute<undefined, OnpremResponse>(
    "onprem/pathfinder/tilesets/ingest",
    "POST"
  ),

  // Other Settings Routes
  ONPREM_SETTINGS_RTSP_GET: createRoute<undefined, OnpremResponseRtspSettings>(
    "onprem/settings/rtsp",
    "GET"
  ),
  ONPREM_SETTINGS_RTSP_SET: createRoute<OnpremRequestRtspSettings, OnpremResponse>(
    "onprem/settings/rtsp",
    "POST"
  ),
  ONPREM_SETTINGS_REMOTE_ID_SET: createRoute<OnpremRequestRemoteIdSettings, OnpremResponse>(
    "onprem/settings/remote-id",
    "POST"
  ),
  ONPREM_SETTINGS_DOCKLESS_SITES_SET: createRoute<
    OnpremRequestDocklessSitesSettings,
    OnpremResponse
  >("onprem/settings/dockless-sites", "POST"),
  ONPREM_SETTINGS_PATHFINDER_SET: createRoute<OnpremRequestPathfinderSettings, OnpremResponse>(
    "onprem/settings/pathfinder",
    "POST"
  ),

  // Feature Flags Routes
  ONPREM_FEATURE_FLAGS_GET: createRoute<
    { feature_flags: string; org_uuid: string },
    OnpremResponseFeatureFlags
  >("onprem/feature-flags", "GET"),
  ONPREM_FEATURE_FLAGS_SET: createRoute<
    { feature_flags: Record<string, boolean>; org_uuid: string },
    OnpremResponseFeatureFlags
  >("onprem/feature-flags", "POST"),
};

export default cloudApiEndpoints;
